import { Clapperboard, PenLine, Scissors, ArrowRight, Check, Lock } from "lucide-react";
import { ROLE_DETAILS } from "@/constants/roles";

const ICONS = {
  "Standard Animator": Clapperboard,
  "High-Quality Animator": Clapperboard,
  "Script Writer": PenLine,
  "Video Editor": Scissors,
};

const roleNames = Object.keys(ROLE_DETAILS);

export const RolesSection = ({ onViewDetails, roleStatus }) => {
  const isClosed = (name) => roleStatus[name] === false;

  return (
    <section id="roles" className="bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="text-center">
          <p className="font-mono-label text-xs font-bold uppercase tracking-widest text-cyan-600">Open positions</p>
          <h2 data-testid="roles-heading" className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl md:text-4xl">
            Three ways to join the crew
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-slate-600">
            Every role is remote and paid per job. Tap a card to see exactly what we're looking for.
          </p>
        </div>

        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {roleNames.map((name) => {
            const role = ROLE_DETAILS[name];
            const Icon = ICONS[name];
            const closed = isClosed(name);
            return (
              <button
                key={role.id}
                data-testid={`role-card-${role.id}`}
                onClick={() => onViewDetails(name)}
                className={`group relative flex flex-col rounded-3xl border bg-white/90 p-8 text-left shadow-sm backdrop-blur-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                  closed ? "border-slate-200/80 opacity-80 hover:border-slate-300" : "border-slate-200/80 hover:border-cyan-200"
                }`}
              >
                {closed && (
                  <span
                    data-testid={`role-closed-badge-${role.id}`}
                    className="absolute right-5 top-5 inline-flex items-center gap-1.5 rounded-full bg-rose-50 border border-rose-200 px-3 py-1 text-[11px] font-bold text-rose-600"
                  >
                    <Lock className="h-3 w-3" /> Closed
                  </span>
                )}
                <div className="flex items-center justify-between">
                  <span className={`flex h-12 w-12 items-center justify-center rounded-2xl transition-colors ${
                    closed ? "bg-slate-100 text-slate-400" : "bg-cyan-50 text-cyan-600 group-hover:bg-cyan-500 group-hover:text-white"
                  }`}>
                    <Icon className="h-6 w-6" />
                  </span>
                  {!closed && (
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      {role.category}
                    </span>
                  )}
                </div>
                <h3 className="mt-6 font-display text-xl font-semibold text-slate-900 sm:text-2xl">{name}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{role.tagline}</p>
                <ul className="mt-5 space-y-2">
                  {role.lookingFor.slice(0, 2).map((item) => (
                    <li key={item} className="flex items-start gap-2 text-xs text-slate-500">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyan-500" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="mt-7 flex items-center justify-between border-t border-slate-100 pt-5">
                  <span className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-bold ${
                    closed ? "bg-slate-100 text-slate-500" : "bg-cyan-50 text-cyan-700"
                  }`}>
                    {role.pay}
                  </span>
                  <span
                    data-testid={`view-role-details-button-${role.id}`}
                    className={`flex items-center gap-1.5 text-sm font-semibold transition-transform group-hover:translate-x-0.5 ${
                      closed ? "text-slate-400" : "text-cyan-600"
                    }`}
                  >
                    Details <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};
